import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Debounced autosave hook.
 * - value: current data to persist
 * - onSave: async (value) => void
 * - delay: ms of inactivity before saving (default 1000)
 * Returns { status: 'idle' | 'saving' | 'saved' | 'error', save: () => Promise<void> }
 * `save()` flushes immediately (useful for explicit Save button).
 */
export function useAutosave(value, onSave, delay = 1000, enabled = true) {
  const [status, setStatus] = useState("idle");
  const timer = useRef(null);
  const latest = useRef(value);
  const initialized = useRef(false);

  useEffect(() => {
    latest.current = value;
  }, [value]);

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    try {
      setStatus("saving");
      await onSave(latest.current);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, [onSave]);

  useEffect(() => {
    if (!enabled) return;
    if (!initialized.current) {
      initialized.current = true;
      return; // skip first render
    }
    if (timer.current) clearTimeout(timer.current);
    setStatus("saving");
    timer.current = setTimeout(() => {
      flush();
    }, delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, enabled, delay, flush]);

  return { status, save: flush };
}
